import { useEffect, useRef, useState, type JSX } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector, useStore } from "react-redux";
import * as Sentry from "@sentry/react";
import type { AppDispatch, RootState } from "@/store/store";
import userService from "@/endpoints/userService";
import ShowToaster from "@/toaster";
import { setPendingSelectionIfChanged } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { parseURLParams } from "@/utils/url";
import { BRANDS, SECONDARY_BRANDS, SEGMENTS } from "@/constant/constantsThrougoutProject";
import {
  buildValidateRequest,
  type SelectionsRefOrigin,
  type TruncatedKinds,
} from "@/utils/selections/buildValidateRequest";
import {
  partitionSelections,
  hasAdoptable,
  hasBlocked,
  type SelectionPartition,
} from "@/utils/selections/partitionSelections";
import { resolveLinkBrandSelection } from "@/utils/selections/resolveLinkBrandSelection";
import { isBrandSetApplicableInView } from "@/utils/selections/brandSetApplicability";
import { resolveLinkSegmentSelection } from "@/utils/selections/resolveLinkSegmentSelection";
import { MAX_APPLIED_SEGMENTS } from "@/components/projectCommon/Filters/reducer/appliedSegmentsReducer";
import { selectAppliedSegmentCodes } from "@/store/slices/segments/selectors";
import { applyLinkSegmentsLocal } from "@/store/slices/segments/segments.thunk";
import { enterSmartLink } from "@/store/slices/smartLink/smartLinkSlice";
import { selectViewDetail } from "@/store/slices/viewsV2/viewsV2Slice";
import { isViewsV2Enabled } from "@/utils/viewDefinition/flag";
import type { CONFIG_SCHEMA } from "@/store/slices/screenConfig/screenConfigSlice";
import type { BrandSetCheck } from "@/interfaces/selections/selections.types";
import type { FilterSelection } from "@/interfaces/filters/domain";
import SelectionAdoptionModal from "./SelectionAdoptionModal";
import { useAdoptSelections } from "./useAdoptSelections";

// Fire the read-path validate at most once per session; a module-level guard
// (not a ref) so it survives route remounts.
let hasRunThisSession = false;

const isEnabled = (): boolean =>
  import.meta.env.VITE_FLAG_SELECTIONS_VALIDATION === "true";

/**
 * Read-path orchestrator (Phase 1) + adoption modal wiring (Phase 2). On the first app
 * load carrying a shared selection, validates the URL's references against the caller's
 * access, silently re-selects the brand sets the caller already owns (mapping a shared
 * source ULID to the caller's own copy), and opens the adoption modal when the response
 * carries ELIGIBLE (adoptable) or NOT_ELIGIBLE (blocked) items to surface.
 * Gated behind VITE_FLAG_SELECTIONS_VALIDATION (default off) so it is inert until rollout.
 * featureStatus.brandSet.enabled gates ONLY the brand-specific work (brand REPLACE + the
 * link's brand refs) -- running that while brand sets are disabled would race the
 * selectionListener's URL strip of brand-set ULIDs. The segment/filter restore has no such
 * race and runs regardless, so the two flags stay otherwise uncoupled (one build-time, one
 * runtime/per-tenant).
 */
const ValidateSelections = (): JSX.Element | null => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const location = useLocation();
  const isDataFetched = useSelector(
    (s: RootState) => s.activeGroup.bootstrapDataStatus.isDataFetched,
  );
  const filtersReady = useSelector(
    (s: RootState) => s.pendingSelection.filtersReady,
  );
  const segmentsReady = useSelector(
    (s: RootState) => s.pendingSelection.segmentsReady,
  );
  // VITE_FLAG_SELECTIONS_VALIDATION (build-time) and featureStatus.brandSet.enabled
  // (runtime, backend-driven per tenant) are independent flags with no code-level
  // coupling. If validation were to run while brand sets are disabled, the
  // selectionListener strips brand-set ULIDs from the URL/selection (see
  // selectionListener.ts's "Strip brand-set ULIDs from URL when feature is
  // disabled" block), so the shared link's ULID would already be gone -- guard
  // here rather than relying on rollout config to keep the two in sync.
  const brandSetEnabled = useSelector(
    (s: RootState) => s.featureStatus?.brandSet?.enabled === true,
  );
  // Subscribed (not a one-off store.getState() read) because these feed the modal's
  // render-time cap/headroom: screenConfig can populate after this component's first
  // render (selectionListener sets it on selection commit), and applied segments can
  // change while the modal is open — both need to force a re-render to stay correct.
  const segmentFilterModalMode = useSelector(
    (s: RootState) => (s.screenConfig as CONFIG_SCHEMA | undefined)?.segmentFilterModalMode,
  );
  const appliedSegmentCodes = useSelector(selectAppliedSegmentCodes);
  const [modalState, setModalState] = useState<{
    partition: SelectionPartition;
    origin: SelectionsRefOrigin;
    truncated: TruncatedKinds;
    linkBrands: (number | string)[];
    linkSecondaryBrands: (number | string)[];
  } | null>(null);
  const { adopt, isApplying, errorMessage } = useAdoptSelections();

  // Capture the URL as seen at first render. The selection listener rewrites the
  // URL during bootstrap (dropping a not-yet-owned brand-set ULID), so reading
  // location.search later — in onAdd — would miss the very refs we must replay.
  const initialSearchRef = useRef(location.search);
  // Same rationale as initialSearchRef: capture the pathname at first render so
  // the smart-link session records where the link was actually opened, before
  // the selection listener can rewrite the URL during bootstrap.
  const initialPathRef = useRef(location.pathname);

  // The link owns the selection: resolve the captured link refs into
  // recipient-space (adopted mapping first, then owned), drop any set that
  // cannot be shown in the current view, and REPLACE the selection with the
  // result. Per-field non-empty guard: never dispatch an empty list — leaving
  // the recipient's seeded default in place is the correct dismiss/decline
  // behaviour. Returns the resolved-then-kept ids (what actually got
  // dispatched) so callers can tell whether a set they confirmed adopting
  // actually made it into the selection, e.g. to warn honestly when it didn't.
  const applyResolvedSelection = (
    linkBrands: (number | string)[],
    linkSecondaryBrands: (number | string)[],
    ownedMapping: Record<string, string>,
    adoptedMapping: Record<string, string>,
  ): (number | string)[] => {
    const resolved = resolveLinkBrandSelection({
      linkBrands,
      linkSecondaryBrands,
      ownedMapping,
      adoptedMapping,
    });
    const state = store.getState();
    const selectedMarket = state.userSelection.markets?.[0] as number | undefined;
    const entities = state.activeGroup.brands.entities;
    // screenConfig's slice-level type is `CONFIG_SCHEMA | {}` (empty before any
    // route registers its config), so cast through CONFIG_SCHEMA as the rest of
    // the codebase does. These flags pick which market-filter branch the listener
    // applies to the brand pool; the predicate mirrors that branch selection.
    const config = state.screenConfig as CONFIG_SCHEMA | undefined;
    const filterFlags = {
      isBrandsFilteredByReputationMarket: config?.isBrandsFilteredByReputationMarket,
      isBrandsFilteredByOwnReputationMarkets:
        config?.isBrandsFilteredByOwnReputationMarkets,
      isBrandsFilteredByMarket: config?.isBrandsFilteredByMarket,
    };
    // Plain numeric brand IDs are the listener's prune to validate; only
    // resolved brand-set ULIDs are filtered for view applicability here.
    const keep = (id: number | string): boolean =>
      typeof id === "number" ||
      isBrandSetApplicableInView(entities[id], selectedMarket, filterFlags);
    const brands = resolved.brands.filter(keep);
    const secondaryBrands = resolved.secondaryBrands.filter(keep);
    const payload: Record<string, (number | string)[]> = {};
    if (brands.length) payload.brands = brands;
    if (secondaryBrands.length) payload.secondaryBrands = secondaryBrands;
    if (Object.keys(payload).length) {
      dispatch(setPendingSelectionIfChanged(payload));
    }
    // NOTE: this is the PRE-CAP list — what we dispatched, before the listener's
    // `maximumAllowedBrands` eviction. If a link carries plain brands ahead of a
    // set beyond the cap, a set reported here as "kept" can still be evicted, so
    // the honest-toast check off this value can be a false negative in that case.
    return [...brands, ...secondaryBrands];
  };

  // Source→result map for the sets the caller already owns (an ALREADY_HAVE set
  // maps a shared source ULID to the caller's own copy; OWNED maps to itself).
  const buildOwnedMapping = (usable: BrandSetCheck[]): Record<string, string> =>
    Object.fromEntries(usable.map((c) => [c.ulid, c.existingUlid ?? c.ulid]));

  useEffect(() => {
    // Wait for the bootstrap selection to commit (it blocks on filters+segments
    // readiness) before resolving the link: applyResolvedSelection reads the
    // committed market to decide which shared sets are applicable in this view.
    // Gate-split (I3): the restore effect runs whenever validation is enabled
    // and the bootstrap selection has committed. `brandSetEnabled` gates ONLY
    // the brand-specific work below (the brand-ULID URL-strip race is brand-only)
    // -- segments and filters have no such race and must restore even with brand
    // sets off.
    if (
      !isEnabled() ||
      hasRunThisSession ||
      !isDataFetched ||
      !filtersReady ||
      !segmentsReady
    )
      return;

    const { request, origin, truncated } = buildValidateRequest(
      new URLSearchParams(initialSearchRef.current),
    );
    // Gate-split (I3): brand-set refs are brand-specific work. When brand sets
    // are disabled, drop them BEFORE validating -- `initialSearchRef` captured
    // the URL before the listener stripped the ULIDs, so a cross-tenant link
    // (enabled sender -> disabled recipient) would otherwise validate and
    // surface a brand-set adoption the recipient's tenant can't use. Segment/
    // filter refs are untouched.
    if (!brandSetEnabled) {
      delete request.brandSets;
    }
    // Re-checked AFTER the brand-set drop so a pure brand-set-ULID link on a
    // disabled tenant correctly no-ops instead of firing an empty validate.
    if (
      !request.brands &&
      !request.brandSets &&
      !request.filters &&
      !request.segments
    ) {
      return;
    }

    hasRunThisSession = true;

    // Parse the link's ordered brand refs from the same captured URL so onAdd
    // replays exactly what buildValidateRequest validated (numbers pass through;
    // strings are brand-set ULIDs). Brand refs are only meaningful when brand
    // sets are enabled -- otherwise the listener has already stripped the ULIDs.
    const parsedLink = parseURLParams(new URLSearchParams(initialSearchRef.current));
    const linkBrands =
      brandSetEnabled && Array.isArray(parsedLink[BRANDS])
        ? (parsedLink[BRANDS] as (number | string)[])
        : [];
    const linkSecondaryBrands =
      brandSetEnabled && Array.isArray(parsedLink[SECONDARY_BRANDS])
        ? (parsedLink[SECONDARY_BRANDS] as (number | string)[])
        : [];
    const linkSegmentCodes = Array.isArray(parsedLink[SEGMENTS])
      ? (parsedLink[SEGMENTS] as string[])
      : [];
    const enteredPath = initialPathRef.current;
    // A `viewId` does NOT mean "the caller's own view": Copy-URL-to-Share copies
    // `window.location.href` and `viewId` is a preserved param, so every shared link
    // carries one. It only tells us a saved view may have its own provenance surface.
    const landingViewId = new URLSearchParams(initialSearchRef.current).get("viewId");

    void (async () => {
      try {
        const response = await userService.selections.validate(request);
        // Single partition call, reused below for both the owned-brand-set
        // auto-selection (Phase 1) and the adoption modal (Phase 2).
        const partition = partitionSelections(response);

        // Gate-split (I3): with brand sets disabled, strip every brand-set
        // verdict so they can neither open the adoption modal nor feed the
        // silent REPLACE. The request already dropped `brandSets`, but a mocked
        // or stale response could still carry them -- belt and braces. Segment
        // and filter verdicts are left intact.
        if (!brandSetEnabled) {
          partition.usable.brandSets = [];
          partition.adoptable.brandSets = [];
          partition.blocked.brandSets = [];
        }

        // Filter enforcement (block-whole): a filter the partition classifies
        // as blocked -- itself inaccessible, or carrying any inaccessible
        // option -- is dropped from the applied selection entirely, never
        // reduced to its accessible options. Runs regardless of
        // brandSetEnabled (I3 gate-split): the filter-accessibility gap has
        // no brand-set URL-strip race, same as the segment restore below.
        const blockedFilterCodes = new Set(
          partition.blocked.filters.map((f) => f.code),
        );
        if (blockedFilterCodes.size) {
          const currentFilters = (store.getState().userSelection.filters ??
            []) as unknown as FilterSelection[];
          const keptFilters = currentFilters.filter(
            (f) => !blockedFilterCodes.has(f.code),
          );
          if (keptFilters.length !== currentFilters.length) {
            dispatch(
              setPendingSelectionIfChanged({
                filters: keptFilters,
              }),
            );
          }
        }

        // A blocked segment/filter always sets `modalWillOpen` (hasBlocked
        // covers them), so the modal already lists those -- the genuine
        // silent case is items dropped from the applied selection that
        // never reach the blocked/adoptable buckets at all: pre-validate
        // truncation (link exceeded the request cap; see `truncated` below)
        // and the owned-segment restore's own applied-segment cap (below).
        const modalWillOpen = hasAdoptable(partition) || hasBlocked(partition);
        // On a saved-view landing the view's own "Limited access" modal already names
        // everything blocked, so a second dialog is a duplicate in shared-link framing.
        // Adoptable items are different: this modal is the ONLY surface that can restore
        // them, and the recipient of a shared view link gets no provenance modal at all
        // (the detail GET 403s), so it must still open whenever something is adoptable.
        const suppressAdoptionModal =
          isViewsV2Enabled() && landingViewId !== null && !hasAdoptable(partition);
        if (modalWillOpen && !suppressAdoptionModal) {
          setModalState({
            partition,
            origin,
            truncated,
            linkBrands,
            linkSecondaryBrands,
          });
        }
        let silentlyDropped = truncated.segments || truncated.filters;

        // Owned/silent path: REPLACE the selection from the link using only the
        // sets the caller already owns. Runs whether or not the modal shows; an
        // empty resolved list dispatches nothing, so the recipient's seed stays.
        // Brand-specific -- gated behind brandSetEnabled (I3 gate-split).
        if (brandSetEnabled) {
          applyResolvedSelection(
            linkBrands,
            linkSecondaryBrands,
            buildOwnedMapping(partition.usable.brandSets),
            {},
          );
        }

        // Segment restore runs regardless of brandSetEnabled and regardless of
        // whether any brand set resolved above. Use the ordered resolver for both
        // branches: usable (OWNED/ALREADY_HAVE/ACCESSIBLE) resolve to the caller's
        // own code, order preserved, first occurrence wins.
        const resolvedSegments = resolveLinkSegmentSelection({
          linkSegmentCodes,
          partition,
          adoptedMapping: {},
        });
        // Read fresh here rather than closing over the subscribed `segmentFilterModalMode`
        // above: the validate request just awaited a network round trip, during which
        // screenConfig can have populated, so the dispatch decision needs the state as
        // of right now, not as of first render. screenConfig's slice-level type is
        // `CONFIG_SCHEMA | {}` (empty before any route registers its config), so cast
        // through CONFIG_SCHEMA as the rest of the codebase does (useAdoptSelections.ts).
        const modeAtDispatch = (store.getState().screenConfig as CONFIG_SCHEMA | undefined)
          ?.segmentFilterModalMode;
        if (modeAtDispatch === "SEGMENT_ONLY") {
          // View-only: snapshot the recipient's real applied set, enter the
          // smart-link session, then local-REPLACE isApplied to exactly the
          // link's segments -- NO patchPreference, so the account is untouched.
          const resolved = resolvedSegments.slice(0, MAX_APPLIED_SEGMENTS);
          // Every one of these segments is already owned/usable (never
          // blocked, never adoptable), so a cap-driven cut here can't have
          // triggered `modalWillOpen` -- it's a genuine silent drop.
          if (resolved.length < resolvedSegments.length) silentlyDropped = true;
          if (resolved.length) {
            // A cached detail for the landing viewId means the GET succeeded, so the view
            // belongs to this caller and calling it "shared" is wrong. The session itself
            // still runs -- it owns re-assertion after a catalog refresh -- only its
            // origin changes. Someone else's view link 403s and caches nothing, so it
            // stays "share".
            const isOwnView =
              isViewsV2Enabled() &&
              landingViewId !== null &&
              selectViewDetail(store.getState(), landingViewId) !== undefined;
            const snapshot = selectAppliedSegmentCodes(store.getState());
            dispatch(
              enterSmartLink({
                snapshotApplied: snapshot,
                linkSegments: resolved,
                enteredPath,
                origin: isOwnView ? "view" : "share",
              }),
            );
            dispatch(applyLinkSegmentsLocal(resolved));
          }
        } else if (resolvedSegments.length) {
          // Segment-as-filter screens: REPLACE the committed list (the link owns
          // the view), ordered; no persistence is involved on these screens.
          dispatch(setPendingSelectionIfChanged({ segments: resolvedSegments }));
        }

        // Honest degradation: the modal is the primary signal for anything it
        // lists (adoptable or blocked). When it doesn't open at all, a link
        // that still couldn't be fully reproduced -- something truncated
        // before validate, or cut by the applied-segment cap -- would
        // otherwise degrade the view with zero indication. Fire at most once.
        if ((!modalWillOpen || suppressAdoptionModal) && silentlyDropped) {
          ShowToaster.warning(
            "Some settings in this link aren't available to you and were left out.",
          );
        }
      } catch (err) {
        // Read path is best-effort: capture, but never block the app.
        Sentry.captureException(err, {
          tags: { feature: "selections", op: "validate" },
        });
      }
    })();
  }, [
    isDataFetched,
    filtersReady,
    segmentsReady,
    brandSetEnabled,
    dispatch,
    store,
  ]);

  if (!modalState) return null;

  const segmentHeadroom =
    segmentFilterModalMode === "SEGMENT_ONLY"
      ? Math.max(0, MAX_APPLIED_SEGMENTS - appliedSegmentCodes.length)
      : undefined;

  return (
    <SelectionAdoptionModal
      open
      partition={modalState.partition}
      truncated={modalState.truncated}
      segmentHeadroom={segmentHeadroom}
      isApplying={isApplying}
      errorMessage={errorMessage}
      onClose={() => setModalState(null)}
      onAdd={async (brandSetUlids, segmentCodes) => {
        const { ok, brandSetMapping } = await adopt(
          brandSetUlids,
          segmentCodes,
          modalState.origin,
          // Same "where the link was opened" semantics as the owned-segment
          // restore's enteredPath: a confirmed adoption that opens the
          // smart-link session (no owned segment resolved one already)
          // should record the link's landing path, not wherever the
          // recipient happens to be when they click Add.
          initialPathRef.current,
        );
        if (ok) {
          const kept = applyResolvedSelection(
            modalState.linkBrands,
            modalState.linkSecondaryBrands,
            buildOwnedMapping(modalState.partition.usable.brandSets),
            brandSetMapping,
          );
          // Honest-toast check: the user confirmed brand sets to adopt, but
          // none of them ended up in the kept selection. Covers both failure
          // modes uniformly — hydration failure (brandSetMapping[ulid] is
          // undefined, so it can never be in `kept`) and the applicability
          // filter (hydration succeeded but the resolved id got dropped by
          // isBrandSetApplicableInView). Keyed off "confirmed but nothing
          // selected", not off a non-empty mapping, so an empty mapping still
          // warns instead of silently passing.
          const keptIds = new Set(kept);
          const adoptedButNotSelected =
            brandSetUlids.length > 0 &&
            !brandSetUlids.some((ulid) => keptIds.has(brandSetMapping[ulid]));
          if (adoptedButNotSelected) {
            ShowToaster.warning(
              "Added to your account, but it isn't available in this market or view.",
            );
          }
          setModalState(null);
        }
      }}
    />
  );
};

export default ValidateSelections;
